import { createMuiTheme, alpha } from '@material-ui/core/styles';
import presets from './theme-presets';
import { basePalettes } from './theme-palettes';

// Every colour in the UI comes from this file.
//
// Components import `colors` and `scrims` and use them exactly like colour
// strings, but the values are CSS custom properties (var(--gx-…)). The real
// values for the active mode are written onto the document root, so switching
// mode repaints the whole app without a single component re-reading anything.
// That is what makes the dark/light toggle possible: a build-time constant could
// not change after load.
//
// Three layers resolve a token, each beating the one below:
//   1. an explicit REACT_APP_THEME_* variable (applies to both modes)
//   2. the REACT_APP_THEME_PRESET palette, for the mode that preset declares
//   3. the base palette for that mode, in theme-palettes.js
//
// The reads below are written out one by one on purpose: webpack's DefinePlugin
// only substitutes literal process.env member expressions, so building the names
// dynamically would leave every override undefined.
//
// Belt colours are deliberately NOT here. They live in components/atom/belt.js
// because they are domain data, not decoration: a blue belt is blue whatever the
// palette says.
const fromEnv = {
  primary: process.env.REACT_APP_THEME_PRIMARY,
  primaryLight: process.env.REACT_APP_THEME_PRIMARY_LIGHT,
  primaryDark: process.env.REACT_APP_THEME_PRIMARY_DARK,
  onPrimary: process.env.REACT_APP_THEME_ON_PRIMARY,
  accent: process.env.REACT_APP_THEME_ACCENT,

  surface: process.env.REACT_APP_THEME_SURFACE,
  surfaceRaised: process.env.REACT_APP_THEME_SURFACE_RAISED,
  surfaceSunken: process.env.REACT_APP_THEME_SURFACE_SUNKEN,
  surfaceAccent: process.env.REACT_APP_THEME_SURFACE_ACCENT,
  surfaceMuted: process.env.REACT_APP_THEME_SURFACE_MUTED,
  surfaceHover: process.env.REACT_APP_THEME_SURFACE_HOVER,
  surfaceSelected: process.env.REACT_APP_THEME_SURFACE_SELECTED,
  surfaceLight: process.env.REACT_APP_THEME_SURFACE_LIGHT,
  surfaceInfo: process.env.REACT_APP_THEME_SURFACE_INFO,
  surfaceContrast: process.env.REACT_APP_THEME_SURFACE_CONTRAST,
  surfaceInverse: process.env.REACT_APP_THEME_SURFACE_INVERSE,

  border: process.env.REACT_APP_THEME_BORDER,
  borderMuted: process.env.REACT_APP_THEME_BORDER_MUTED,
  borderStrong: process.env.REACT_APP_THEME_BORDER_STRONG,

  textPrimary: process.env.REACT_APP_THEME_TEXT_PRIMARY,
  textMuted: process.env.REACT_APP_THEME_TEXT_MUTED,
  onScrim: process.env.REACT_APP_THEME_ON_SCRIM,
  link: process.env.REACT_APP_THEME_LINK,

  danger: process.env.REACT_APP_THEME_DANGER,
  dangerMuted: process.env.REACT_APP_THEME_DANGER_MUTED,
  dangerStrong: process.env.REACT_APP_THEME_DANGER_STRONG,
  dangerBright: process.env.REACT_APP_THEME_DANGER_BRIGHT,
  success: process.env.REACT_APP_THEME_SUCCESS,
  successLight: process.env.REACT_APP_THEME_SUCCESS_LIGHT,

  chartOne: process.env.REACT_APP_THEME_CHART_ONE,
  chartTwo: process.env.REACT_APP_THEME_CHART_TWO,
  chartThree: process.env.REACT_APP_THEME_CHART_THREE,
  chartFour: process.env.REACT_APP_THEME_CHART_FOUR,
};

const CHART_KEYS = ['one', 'two', 'three', 'four'];
const ENV_CHART_KEYS = {
  one: 'chartOne',
  two: 'chartTwo',
  three: 'chartThree',
  four: 'chartFour',
};

const presetName = (process.env.REACT_APP_THEME_PRESET || '').trim();
const preset = presets[presetName] || {};

if (presetName && !presets[presetName]) {
  // Worth shouting about: a typo here silently ships the base palettes.
  console.warn(
    `Unknown REACT_APP_THEME_PRESET "${presetName}". Known presets: ${Object.keys(
      presets
    ).join(', ')}. Falling back to the built-in palettes.`
  );
}

const usable = (value) => typeof value === 'string' && !!value.trim();

// The mode a preset applies to, and the mode the app starts in.
export const defaultMode = preset.mode === 'light' ? 'light' : 'dark';
export const MODES = ['dark', 'light'];

const resolvePalette = (mode) => {
  const base = basePalettes[mode];
  const presetColors = preset.mode === mode ? preset.colors || {} : {};
  const layer = (key, envKey) => {
    const envValue = fromEnv[envKey || key];
    if (usable(envValue)) return envValue.trim();
    if (usable(presetColors[key])) return presetColors[key].trim();
    return base[key];
  };

  const resolved = {};
  Object.keys(base).forEach((key) => {
    if (key === 'chart') return;
    resolved[key] = layer(key);
  });

  resolved.chart = {};
  CHART_KEYS.forEach((key) => {
    const envValue = fromEnv[ENV_CHART_KEYS[key]];
    const presetChart = (presetColors.chart || {})[key];
    resolved.chart[key] = usable(envValue)
      ? envValue.trim()
      : usable(presetChart)
      ? presetChart.trim()
      : base.chart[key];
  });

  return resolved;
};

// A translucent wash of the brand colour for the news feed's glow, derived so it
// tracks whatever primary resolves to. alpha() only parses hex/rgb/hsl, so a
// named colour falls back to a plain black wash.
const glowFor = (value) => {
  try {
    return alpha(value, 0.35);
  } catch (e) {
    return 'rgba(0, 0, 0, 0.35)';
  }
};

const scrimsFor = (mode, palette) => ({
  soft: 'rgba(0, 0, 0, 0.2)',
  medium: 'rgba(0, 0, 0, 0.4)',
  strong: 'rgba(0, 0, 0, 0.6)',
  modal: 'rgba(0, 0, 0, 0.72)',
  shadow: 'rgba(0, 0, 0, 0.75)',
  // The odd one out: a wash used to lift a panel off its background, so it has
  // to invert with the mode or it is invisible on a light ground.
  highlight:
    mode === 'light' ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.08)',
  primaryGlow: glowFor(palette.primary),
});

// Real values, per mode. These feed Material UI's palette and the CSS variables.
export const palettes = MODES.reduce((acc, mode) => {
  const palette = resolvePalette(mode);
  acc[mode] = { colors: palette, scrims: scrimsFor(mode, palette) };
  return acc;
}, {});

const kebab = (key) => key.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);
const colorVar = (key) => `--gx-${kebab(key)}`;
const chartVar = (key) => `--gx-chart-${key}`;
const scrimVar = (key) => `--gx-scrim-${kebab(key)}`;

// The token objects components import. Same shape as the real palettes, but every
// value is a reference to the custom property rather than a colour.
const reference = (name) => `var(${name})`;

export const colors = Object.keys(basePalettes.dark).reduce(
  (acc, key) => {
    if (key === 'chart') return acc;
    acc[key] = reference(colorVar(key));
    return acc;
  },
  {
    chart: CHART_KEYS.reduce((acc, key) => {
      acc[key] = reference(chartVar(key));
      return acc;
    }, {}),
  }
);

export const scrims = Object.keys(palettes[defaultMode].scrims).reduce(
  (acc, key) => {
    acc[key] = reference(scrimVar(key));
    return acc;
  },
  {}
);

// The custom properties for one mode, as a plain map.
export const cssVariablesFor = (mode) => {
  const { colors: real, scrims: realScrims } = palettes[mode] || palettes[defaultMode];
  const vars = {};
  Object.keys(real).forEach((key) => {
    if (key === 'chart') return;
    vars[colorVar(key)] = real[key];
  });
  CHART_KEYS.forEach((key) => {
    vars[chartVar(key)] = real.chart[key];
  });
  Object.keys(realScrims).forEach((key) => {
    vars[scrimVar(key)] = realScrims[key];
  });
  return vars;
};

const STORAGE_KEY = 'gym-theme-mode';

export const isMode = (value) => MODES.indexOf(value) !== -1;

// Reading and writing the choice is wrapped because Safari private browsing
// throws on localStorage rather than returning null.
export const readStoredMode = () => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isMode(stored) ? stored : null;
  } catch (e) {
    return null;
  }
};

export const storeMode = (mode) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, mode);
  } catch (e) {
    // A saved preference is a convenience, not a requirement.
  }
};

// Writes one mode's values onto the document root. Called before React renders so
// the first paint is already in the right mode, and again on every toggle.
export const applyThemeMode = (mode) => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const vars = cssVariablesFor(mode);
  Object.keys(vars).forEach((name) => {
    root.style.setProperty(name, vars[name]);
  });
  root.setAttribute('data-theme', mode);
  // Lets the browser theme scrollbars and form controls to match.
  root.style.colorScheme = mode;
};

export const initialMode = readStoredMode() || defaultMode;

applyThemeMode(initialMode);

export const buildTheme = (mode) => {
  const real = (palettes[mode] || palettes[defaultMode]).colors;

  return createMuiTheme({
    // Hung on the theme so components can reach tokens through either provider:
    // useTheme().colors.x for Material UI, ({ theme }) => theme.colors.x for
    // styled-components. These are the custom-property references, so they stay
    // correct across a mode change.
    colors,
    scrims,
    mode,

    // Material UI does colour maths on these (hover states, contrast text), so
    // unlike components it needs the real values for the active mode.
    //
    // On dark this keeps the shape the palette had before any of this existed:
    // text, divider and error stay on Material UI's own dark defaults, because
    // pointing them at the tokens would restyle every textSecondary Typography,
    // Divider and error state at once. Light has to override them — the dark
    // defaults are white text and a white divider, invisible on a light ground.
    palette: {
      type: mode,
      background: {
        default: real.surface,
        paper: real.surfaceRaised,
      },
      primary: {
        main: real.primary,
        contrastText: real.onPrimary,
      },
      secondary: {
        light: real.primaryLight,
        main: real.primary,
        contrastText: real.accent,
      },
      ...(mode === 'light'
        ? {
            text: {
              primary: real.textPrimary,
              secondary: real.textMuted,
            },
            divider: real.border,
            error: { main: real.dangerStrong },
          }
        : {}),
    },

    typography: {
      // Use the system font instead of the default Roboto font.
      useNextVariants: true,
      fontFamily: [
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
        '"Apple Color Emoji"',
        '"Segoe UI Emoji"',
        '"Segoe UI Symbol"',
      ].join(','),
    },
  });
};

export const themes = MODES.reduce((acc, mode) => {
  acc[mode] = buildTheme(mode);
  return acc;
}, {});

export const theme = themes[initialMode];

export default theme;
